import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn';

/**
 * Button variants using CVA (Class Variance Authority)
 * Defines all visual variations of the Button component
 */
const buttonVariants = cva(
  // Base styles (always applied)
  [
    'inline-flex items-center justify-center gap-2',
    'border-2 border-linkode-text rounded-linkode',
    'font-mono font-bold uppercase',
    'transition-all duration-200',
    'disabled:cursor-not-allowed disabled:opacity-50',
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-linkode-primary text-linkode-text',
          'hover:shadow-linkode-hover hover:translate-x-0.5 hover:-translate-y-0.5',
        ],
        outline: ['bg-transparent text-linkode-text', 'hover:bg-linkode-text hover:text-white'],
        ghost: ['border-transparent bg-transparent text-linkode-text', 'hover:bg-linkode-bg-light'],
      },
      size: {
        sm: 'px-4 py-2 text-sm',
        md: 'px-6 py-3 text-base',
        lg: 'px-8 py-4 text-lg',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    compoundVariants: [
      {
        variant: 'primary',
        size: 'lg',
        className: 'shadow-linkode',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
    },
  }
);

/** Exported type for Button variants */
export type ButtonVariants = VariantProps<typeof buttonVariants>;

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, ButtonVariants {
  /** Loading state */
  isLoading?: boolean;
}

/** Primary button component following Linkode Landing Page design */
export const Button: React.FC<ButtonProps> = ({
  variant,
  size,
  fullWidth,
  isLoading,
  disabled,
  className,
  children,
  ...props
}) => {
  const isDisabled = disabled || isLoading;

  return (
    <button
      className={cn(buttonVariants({ variant, size, fullWidth }), className)}
      disabled={isDisabled}
      aria-busy={isLoading}
      {...props}
    >
      {isLoading && (
        <svg
          className="animate-spin w-5 h-5"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {children}
    </button>
  );
};

// Export buttonVariants for external use (e.g., testing)
export { buttonVariants };
